import { calculateGstBreakdown } from "./gstCalculation";
import { calculateInvoiceCommissionLineMetrics } from "../components/reports/commissions/detail/commissionDiscountUtils";
import {
  calculateInvoiceDiscountAmount,
  isValidCustomDiscountAmount,
} from "./invoiceDiscountHelpers";

export const INVOICE_AMOUNT_MIN_DECIMALS = 2;
export const INVOICE_AMOUNT_MAX_DECIMALS = 2;
export const INVOICE_DISCOUNT_MAX_DECIMALS = 4;

export function roundInvoiceAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const factor = 10 ** INVOICE_AMOUNT_MAX_DECIMALS;
  return Math.round(num * factor) / factor;
}

const roundMoney = roundInvoiceAmount;

export function formatInvoiceAmountInput(value) {
  const num = roundInvoiceAmount(value);
  if (!Number.isFinite(num)) return "";

  let str = num.toFixed(INVOICE_AMOUNT_MAX_DECIMALS);
  if (str.includes(".")) {
    str = str.replace(/0+$/, "").replace(/\.$/, "");
  }

  if (!str.includes(".")) {
    return num.toFixed(INVOICE_AMOUNT_MIN_DECIMALS);
  }

  const decimalPart = str.split(".")[1] || "";
  if (decimalPart.length < INVOICE_AMOUNT_MIN_DECIMALS) {
    return num.toFixed(INVOICE_AMOUNT_MIN_DECIMALS);
  }

  return str;
}

export function normalizeInvoiceAmountInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === ".") return "";
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return "";
  return formatInvoiceAmountInput(parsed);
}

export function normalizeTaxType(value) {
  const status = String(value || "").toLowerCase().trim();
  if (status === "inclusive" || status === "exclusive") return status;
  if (
    status === "notax" ||
    status === "no tax" ||
    status === "none" ||
    status === "no_tax"
  ) {
    return "notax";
  }
  return "";
}

/** Resolve saved/API line tax rate from common field names. */
export function resolveSavedLineTaxRate(row = {}, invoice = null) {
  const direct =
    row.tax_rate ??
    row.taxRate ??
    row.gst_percentage ??
    row.gstPercentage ??
    row.gst_rate;
  if (direct !== null && direct !== undefined && String(direct).trim() !== "") {
    return String(direct);
  }

  const invoiceRate =
    invoice?.gst_percentage ?? invoice?.tax_rate ?? invoice?.gst_rate;
  if (
    invoiceRate !== null &&
    invoiceRate !== undefined &&
    String(invoiceRate).trim() !== ""
  ) {
    return String(invoiceRate);
  }

  return "";
}

/** True when the line row has any saved tax-type field (including explicit no tax). */
export function lineHasExplicitTaxDeclaration(row = {}) {
  const values = [
    row.gst_type,
    row.gstType,
    row.gst_status,
    row.gstStatus,
    row.tax_type,
    row.taxType,
  ];
  return values.some((value) => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== "";
  });
}

/**
 * Resolve tax type from saved/API line items.
 * Per-line fields win; invoice-level tax is only a fallback when the line has none.
 */
export function resolveSavedLineTaxType(row = {}, invoiceTaxType = "") {
  const taxTypeFields = [row.tax_type, row.taxType];
  // Bonus installment records carry gst_type (often inclusive 10). That is
  // not the invoice line tax — only tax_type / gst_status on the line are.
  const gstFields = isBonusInvoiceLine(row)
    ? [row.gst_status, row.gstStatus]
    : [
        row.gst_type,
        row.gstType,
        row.gst_status,
        row.gstStatus,
      ];

  for (const value of taxTypeFields) {
    if (value === null || value === undefined || String(value).trim() === "") {
      continue;
    }
    const normalized = normalizeTaxType(value);
    if (normalized) return normalized;
  }

  for (const value of gstFields) {
    if (value === null || value === undefined || String(value).trim() === "") {
      continue;
    }
    const normalized = normalizeTaxType(value);
    if (normalized) return normalized;
  }

  const taxAmount =
    Number(
      row.tax_amount ?? row.taxAmount ?? row.gst_amount ?? row.gstAmount,
    ) || 0;
  const taxRate = Number(resolveSavedLineTaxRate(row, null)) || 0;
  // Bonus installment gst_percentage is often still 10 after the line was
  // saved as no tax — do not treat that leftover rate as invoice GST.
  if (
    (taxAmount > 0 || taxRate > 0) &&
    !isBonusInvoiceLine(row)
  ) {
    return normalizeTaxType(invoiceTaxType) || "exclusive";
  }
  if (isBonusInvoiceLine(row) && taxAmount > 0) {
    return normalizeTaxType(invoiceTaxType) || "exclusive";
  }

  if (lineHasExplicitTaxDeclaration(row)) {
    return "notax";
  }

  const fromInvoice = normalizeTaxType(invoiceTaxType);
  return fromInvoice || "notax";
}

export function getDefaultTaxTypeFromApplication(
  applicationCost,
  applicationGstInfo,
) {
  const fromGstInfo = normalizeTaxType(applicationGstInfo?.type);
  if (fromGstInfo) return fromGstInfo;

  const fromCost = normalizeTaxType(
    applicationCost?.gst_status || applicationCost?.tax,
  );
  if (fromCost) return fromCost;

  return "notax";
}

export function getApplicationItemName(application) {
  if (!application) return "";

  const packageId = application.package_id ?? application.packageId;
  const courseId = application.course_id ?? application.courseid;
  const isPackage =
    packageId != null ||
    courseId === 0 ||
    courseId === "0" ||
    Boolean(application.package_name || application.packagename);

  if (isPackage) {
    return (
      application.application ||
      application.package_name ||
      application.packagename ||
      application.course_name ||
      application.coursename ||
      application.name ||
      "Application"
    );
  }

  return (
    application.application ||
    application.course_name ||
    application.coursename ||
    application.visa_name ||
    application.visaname ||
    application.package_name ||
    application.packagename ||
    application.name ||
    application.title ||
    "Application"
  );
}

export function buildProfessionalFeesDescription(application) {
  if (!application) return "Professional Fees";

  const typeLabel = isMigrationApplicationType(
    application.application_type ||
      application.applicationType ||
      application.type ||
      application.category,
  )
    ? "Migration"
    : "Education";
  const name = getApplicationItemName(application) || "Application";
  const fee = application.professional_fee || {};
  const currency = fee.currency || application.currency || "AUD";
  const amount = Number(fee.cost || fee.base_cost) || 0;
  const price = `${currency} ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  return `${name} · ${price} · ${typeLabel}`;
}

export function buildDepartmentFeesDescription(application) {
  if (!application) return "Department Fees";
  const name = getApplicationItemName(application) || "Application";
  const fee =
    application.department_fee && typeof application.department_fee === "object"
      ? application.department_fee
      : {};
  const currency =
    fee.currency ||
    application.department_fee_currency ||
    application.currency ||
    "AUD";
  const amount =
    Number(fee.cost || fee.base_cost || application.department_fee) || 0;
  const price = `${currency} ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  return `${name} · ${price} · Migration`;
}

export function isMigrationApplicationType(applicationType) {
  const type = String(applicationType || "").toLowerCase().trim();
  return type === "migration" || type === "mig";
}

export function getDefaultDepartmentTaxType(applicationCost, departmentGstInfo) {
  const fromCost = normalizeTaxType(
    applicationCost?.department_fee_gst_status ||
      applicationCost?.department_gst_status,
  );
  if (fromCost) return fromCost;
  return normalizeTaxType(departmentGstInfo?.type) || "notax";
}

export function resolveDepartmentUnitPrice(
  applicationCost,
  departmentGstInfo,
  taxType,
) {
  const effectiveTaxType = normalizeTaxType(taxType);
  const totalFee = Number(
    applicationCost?.department_fee ?? departmentGstInfo?.totalFee ?? 0,
  );
  const baseFee = Number(
    departmentGstInfo?.baseCost ??
      applicationCost?.department_base_fee ??
      applicationCost?.department_fee_base_cost ??
      0,
  );

  if (effectiveTaxType === "inclusive" && totalFee > 0) return totalFee;
  if (baseFee > 0) return baseFee;
  return totalFee;
}

export function resolveDepartmentGstPercentage(
  applicationCost,
  departmentGstInfo,
) {
  return (
    Number(applicationCost?.department_fee_gst_percentage) ||
    Number(departmentGstInfo?.percentage) ||
    0
  );
}

export function resolveApplicationBaseCost(
  applicationCost,
  applicationGstInfo,
  application,
) {
  const baseFromCost = Number(applicationCost?.base_cost);
  if (baseFromCost > 0) return baseFromCost;

  const baseFromGstInfo = Number(applicationGstInfo?.baseCost);
  if (baseFromGstInfo > 0) return baseFromGstInfo;

  const baseFromApp = Number(application?.base_cost);
  if (baseFromApp > 0) return baseFromApp;

  const professionalCost = Number(
    applicationCost?.professional_cost || application?.professional_cost || 0,
  );
  const taxType = normalizeTaxType(
    applicationGstInfo?.type ||
      applicationCost?.gst_status ||
      application?.gst_status,
  );

  if (taxType === "inclusive" && professionalCost > 0) {
    return professionalCost;
  }

  return professionalCost;
}

export function resolveApplicationUnitPrice(
  applicationCost,
  applicationGstInfo,
  application,
  taxType,
) {
  const effectiveTaxType = normalizeTaxType(taxType);
  const professionalCost = Number(
    applicationCost?.professional_cost || application?.professional_cost || 0,
  );

  if (effectiveTaxType === "inclusive" && professionalCost > 0) {
    return professionalCost;
  }

  return resolveApplicationBaseCost(
    applicationCost,
    applicationGstInfo,
    application,
  );
}

export function resolveApplicationGstPercentage(
  applicationCost,
  applicationGstInfo,
  application,
) {
  return (
    Number(applicationCost?.gst_percentage) ||
    Number(applicationGstInfo?.percentage) ||
    Number(application?.gst_percentage) ||
    0
  );
}

export function resolveLineItemTaxRate(taxType, gstPercentage) {
  const effectiveTaxType = normalizeTaxType(taxType) || "notax";
  if (effectiveTaxType === "notax") return "";

  const rate = Number(gstPercentage) || 0;
  return rate > 0 ? String(rate) : "";
}

export function createDefaultLineDiscountFields() {
  return {
    discount_tab: "predefined",
    discount_rule_id: "",
    discount_custom_amount: "",
    discount_percent: "",
    student_paid_tuition: "",
    discount_reason: "",
    discount: "",
  };
}

export function sanitizePercentInput(value, maxDecimals = 2) {
  if (value === "" || value === null || value === undefined) return "";
  let cleaned = String(value).replace(/[^\d.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex !== -1) {
    const intPart = cleaned.slice(0, dotIndex + 1);
    const decPart = cleaned.slice(dotIndex + 1).replace(/\./g, "");
    cleaned = intPart + decPart.slice(0, maxDecimals);
  }
  return cleaned;
}

export function normalizePercentInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === ".") return "";
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return "";
  return String(Math.min(parsed, 100));
}

/** Discount base respects line tax type (inclusive / exclusive / notax). */
export function calculateLineDiscountFromPercent(row, percent) {
  const pct = Number(percent);
  if (!Number.isFinite(pct) || pct <= 0) return 0;

  const base = getLineTotalBeforeDiscount({
    ...row,
    discount: 0,
    discount_custom_amount: "",
    discount_percent: "",
  });
  if (base <= 0) return 0;

  return Math.min(roundMoney(base * (pct / 100)), base);
}

export function resolveLineDiscountPercent(row) {
  const stored = row?.discount_percent ?? row?.discount_percentage;
  if (stored !== "" && stored != null && stored !== undefined) {
    const pct = Number(stored);
    if (Number.isFinite(pct) && pct >= 0) return pct;
  }

  const discount = Number(row?.discount) || 0;
  if (discount <= 0) return 0;

  const base = getLineTotalBeforeDiscount({
    ...row,
    discount: 0,
    discount_custom_amount: "",
    discount_percent: "",
  });
  if (base <= 0) return 0;

  return roundMoney((discount / base) * 100);
}

/** Commission invoice: user enters %, amount derived from tax-aware line total. */
export function syncCommissionLineDiscountPercent(row) {
  const raw = row.discount_percent ?? "";
  if (raw === "" || raw === null || raw === undefined) {
    return {
      ...row,
      discount_tab: "percentage",
      discount_rule_id: "",
      discount_custom_amount: "",
      discount: "",
    };
  }

  const parsed = Number(raw);
  if (Number.isNaN(parsed) || parsed < 0) {
    return {
      ...row,
      discount_tab: "percentage",
      discount_rule_id: "",
      discount_custom_amount: "",
      discount: "",
    };
  }

  const cappedPercent = Math.min(parsed, 100);
  const amount = calculateLineDiscountFromPercent(row, cappedPercent);

  return {
    ...row,
    discount_tab: "percentage",
    discount_rule_id: "",
    discount_custom_amount: "",
    discount_percent:
      raw === String(row.discount_percent) ? row.discount_percent : String(cappedPercent),
    discount: amount > 0 ? String(amount) : "",
  };
}

export function createCommissionLineDiscountFields() {
  return {
    discount_tab: "percentage",
    discount_rule_id: "",
    discount_custom_amount: "",
    discount_percent: "",
    discount_reason: "",
    discount: "",
  };
}

/** Commission installment / tuition lines: discount is informational only. */
export function isCommissionTuitionInvoiceLine(row = {}) {
  if (
    Number(row.commission_installment_id ?? row.commissionInstallmentId)
  ) {
    return true;
  }
  const feeType = String(row.fee_type ?? row.feeType ?? "").toLowerCase();
  return feeType === "tuition";
}

/** Bonus installment line (linked via bonus_installment_id or fee_type). */
export function isBonusInvoiceLine(row = {}) {
  if (Number(row.bonus_installment_id ?? row.bonusInstallmentId)) {
    return true;
  }
  const feeType = String(row.fee_type ?? row.feeType ?? "").toLowerCase();
  return feeType === "bonus";
}

/** Commission or bonus installment lines — same GST / payment total rules. */
export function isCommissionInstallmentInvoiceLine(row = {}) {
  return isCommissionTuitionInvoiceLine(row) || isBonusInvoiceLine(row);
}

/**
 * Ensure commission/bonus installment lines carry document tax before save.
 * Uses row tax when set; otherwise applies documentTaxType + GST rate.
 */
export function applyDocumentTaxToInstallmentLines(
  lineItems = [],
  documentTaxType = "notax",
  gstPercentage = 0,
) {
  const docTax = normalizeTaxType(documentTaxType) || "notax";
  const docRate =
    docTax === "notax" ? "" : resolveLineItemTaxRate(docTax, gstPercentage);

  return (Array.isArray(lineItems) ? lineItems : []).map((row) => {
    if (!isCommissionInstallmentInvoiceLine(row)) return row;

    const rowTax = normalizeTaxType(row.tax_type);
    const effectiveTax = isBonusInvoiceLine(row)
      ? rowTax || "notax"
      : rowTax || docTax;

    if (effectiveTax === "notax") {
      return { ...row, tax_type: "notax", tax_rate: "" };
    }

    const existingRate = Number(row.tax_rate) || 0;
    const effectiveRate =
      existingRate > 0 ? String(row.tax_rate) : docRate;

    return {
      ...row,
      tax_type: effectiveTax,
      tax_rate: effectiveRate,
    };
  });
}

/** Commission invoice wizard footer totals (create/edit suggestion mode). */
export function computeCommissionSuggestionInvoiceTotals(
  lineItems = [],
  commissionPlanMeta = {},
) {
  const withDiscount = calculateInvoiceLineTotals(lineItems);
  const commissionLineMetrics = (Array.isArray(lineItems) ? lineItems : [])
    .filter(isCommissionTuitionInvoiceLine)
    .map((row) => calculateInvoiceCommissionLineMetrics(row, commissionPlanMeta));
  const sumCommissionMetric = (key) =>
    Math.round(
      commissionLineMetrics.reduce(
        (total, row) => total + (Number(row[key]) || 0),
        0,
      ) * 100,
    ) / 100;
  const commissionMetricTotals = {
    commission_discount_paid_total: sumCommissionMetric("discountPaid"),
    commission_discount_balance_total: sumCommissionMetric("discountBalance"),
    commission_net_commission_total: sumCommissionMetric("netCommission"),
    commission_balance_fee_total: sumCommissionMetric("balanceFee"),
  };

  const appliedDiscountTotal = Number(withDiscount.discount_total) || 0;
  if (appliedDiscountTotal <= 0) {
    return {
      ...withDiscount,
      commission_discount_total: 0,
      ...commissionMetricTotals,
    };
  }

  const lineItemsForTotals = lineItems.map((row) => {
    if (!isCommissionTuitionInvoiceLine(row)) return row;
    return {
      ...row,
      discount: 0,
      discount_custom_amount: "",
    };
  });

  const amountsTotals = calculateInvoiceLineTotals(lineItemsForTotals);
  const commissionDiscountTotal = sumCommissionTuitionLineDiscounts(
    withDiscount.lineItems,
  );

  return {
    ...amountsTotals,
    discount_total: appliedDiscountTotal,
    commission_discount_total: commissionDiscountTotal,
    ...commissionMetricTotals,
    lineItems: amountsTotals.lineItems.map((row, index) => ({
      ...row,
      discount: withDiscount.lineItems[index]?.discount || 0,
      discount_custom_amount:
        lineItems[index]?.discount_custom_amount ??
        withDiscount.lineItems[index]?.discount_custom_amount,
      discount_percent: lineItems[index]?.discount_percent ?? "",
      discount_reason:
        lineItems[index]?.discount_reason ||
        withDiscount.lineItems[index]?.discount_reason ||
        "",
    })),
  };
}

export function isCommissionInvoiceLineItems(lineItems = []) {
  return (Array.isArray(lineItems) ? lineItems : []).some(
    (row) =>
      Number(row.commission_installment_id ?? row.commissionInstallmentId) ||
      Number(row.bonus_installment_id ?? row.bonusInstallmentId),
  );
}

/** GST rate for commission invoice PDF/preview (plain %, empty for no tax). */
export function formatCommissionPreviewGstRate(row = {}) {
  const type = normalizeTaxType(row.tax_type);
  if (type === "notax") return "";
  return formatTaxRateLabel(row.tax_rate, row.tax_type);
}

export function sumCommissionTuitionLineDiscounts(lineItems = []) {
  return roundMoney(
    (Array.isArray(lineItems) ? lineItems : []).reduce((sum, row) => {
      if (!isCommissionTuitionInvoiceLine(row)) return sum;
      return sum + (Number(row.discount) || 0);
    }, 0),
  );
}

export function createEmptyLineItem(id, taxRate = "", taxType = "notax") {
  return {
    id,
    description: "",
    quantity: "1",
    unit_price: "",
    tax_rate: taxRate,
    tax_type: normalizeTaxType(taxType) || "notax",
    ...createDefaultLineDiscountFields(),
  };
}

export function createInitialLineItemsFromApplication({
  application,
  applicationCost,
  applicationGstInfo,
  departmentGstInfo = null,
  applicationType = "",
  taxType,
}) {
  const effectiveTaxType = normalizeTaxType(taxType) || "notax";
  const unitPrice = resolveApplicationUnitPrice(
    applicationCost,
    applicationGstInfo,
    application,
    effectiveTaxType,
  );
  const gstPercentage = resolveApplicationGstPercentage(
    applicationCost,
    applicationGstInfo,
    application,
  );

  const items = [
    {
      id: "line-1",
      description: buildProfessionalFeesDescription(application),
      quantity: "1",
      unit_price: unitPrice > 0 ? String(unitPrice) : "",
      tax_rate: resolveLineItemTaxRate(effectiveTaxType, gstPercentage),
      tax_type: effectiveTaxType,
      unit_price_locked: true,
      tax_type_locked: true,
      ...createDefaultLineDiscountFields(),
    },
  ];

  if (isMigrationApplicationType(applicationType)) {
    const deptTaxType = getDefaultDepartmentTaxType(
      applicationCost,
      departmentGstInfo,
    );
    const deptUnitPrice = resolveDepartmentUnitPrice(
      applicationCost,
      departmentGstInfo,
      deptTaxType,
    );
    const deptGstPercentage = resolveDepartmentGstPercentage(
      applicationCost,
      departmentGstInfo,
    );

    if (deptUnitPrice > 0) {
      items.push({
        id: "line-2",
        description: buildDepartmentFeesDescription(application),
        quantity: "1",
        unit_price: String(deptUnitPrice),
        tax_rate: resolveLineItemTaxRate(deptTaxType, deptGstPercentage),
        tax_type: deptTaxType,
        unit_price_locked: true,
        tax_type_locked: true,
        ...createDefaultLineDiscountFields(),
      });
    }
  }

  return items;
}

export function sanitizeIntegerInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

export function sanitizeDecimalInput(
  value,
  maxDecimals = INVOICE_AMOUNT_MAX_DECIMALS,
) {
  if (value === "" || value === null || value === undefined) return "";
  let cleaned = String(value).replace(/[^\d.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex !== -1) {
    const intPart = cleaned.slice(0, dotIndex + 1);
    const decPart = cleaned.slice(dotIndex + 1).replace(/\./g, "");
    cleaned = intPart + decPart.slice(0, maxDecimals);
  }
  return cleaned;
}

export function normalizeQuantityInput(value) {
  if (value === "" || value === null || value === undefined) return "1";
  const parsed = parseInt(String(value).replace(/\D/g, ""), 10);
  if (!Number.isFinite(parsed) || parsed < 1) return "1";
  return String(parsed);
}

export function normalizeDecimalInput(value) {
  return normalizeInvoiceAmountInput(value);
}

export function sanitizeDiscountDecimalInput(value) {
  return sanitizeDecimalInput(value, INVOICE_DISCOUNT_MAX_DECIMALS);
}

export function normalizeDiscountAmountInput(value) {
  if (value === "" || value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === ".") return "";
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return "";
  const factor = 10 ** INVOICE_DISCOUNT_MAX_DECIMALS;
  const rounded = Math.round(parsed * factor) / factor;
  return String(rounded);
}

export function getLineGrossAmount(row) {
  const qty = Math.max(Number(row?.quantity) || 0, 0);
  const unit = Number(row?.unit_price) || 0;
  return roundMoney(qty * unit);
}

/** Line total shown in the grid: quantity × unit price minus line discount (tax in footer). */
export function getLineDisplayTotalAmount(row = {}) {
  const gross = getLineGrossAmount(row);
  if (isCommissionTuitionInvoiceLine(row)) {
    return gross;
  }
  const discount = Math.max(Number(row?.discount) || 0, 0);
  return Math.max(roundMoney(gross - discount), 0);
}

export function sumLineDisplayTotalAmounts(lineItems = []) {
  return roundMoney(
    (Array.isArray(lineItems) ? lineItems : []).reduce(
      (sum, row) => sum + getLineDisplayTotalAmount(row),
      0,
    ),
  );
}

/** Line total excluding tax (amount − tax_amount). */
export function getLineAmountExTax(row = {}) {
  const amount = Number(row?.amount) || 0;
  const tax = Number(row?.tax_amount) || 0;
  return Math.max(roundMoney(amount - tax), 0);
}

export function sumLineAmountsExTax(lineItems = []) {
  return roundMoney(
    (Array.isArray(lineItems) ? lineItems : []).reduce(
      (sum, row) => sum + getLineAmountExTax(row),
      0,
    ),
  );
}

export function getLineTotalBeforeDiscount(row) {
  const gross = getLineGrossAmount(row);
  if (gross <= 0) return 0;

  const rate = Number(row?.tax_rate) || 0;
  const taxType = normalizeTaxType(row?.tax_type) || "notax";

  if (taxType === "notax" || rate <= 0) {
    return gross;
  }

  const breakdown = calculateGstBreakdown({
    baseCost: gross,
    gstPercentage: rate,
    taxType,
  });

  return taxType === "exclusive"
    ? breakdown.professional_cost
    : breakdown.base_cost;
}

export function isApplicationFeeDescription(description) {
  const desc = String(description || "").trim().toLowerCase();
  return (
    desc.startsWith("professional fees") || desc.startsWith("department fees")
  );
}

export function isApplicationFeeLine(row) {
  return (
    Boolean(row?.unit_price_locked) ||
    isApplicationFeeDescription(row?.description)
  );
}

export function invoiceUsesSegregatedLineDiscounts(
  lineItems = [],
  { isEditMode = false } = {},
) {
  if (lineItems.some((row) => isApplicationFeeLine(row))) return true;

  if (isEditMode && lineItems.length > 0) return true;

  return false;
}

export function resolveLineDiscountAmount(row, rules = []) {
  const lineTotal = getLineTotalBeforeDiscount(row);
  if (lineTotal <= 0) return 0;

  return calculateInvoiceDiscountAmount({
    tab: row.discount_tab || "predefined",
    ruleId: row.discount_rule_id || "",
    rules,
    customAmount: row.discount_custom_amount || "",
    grossSubtotal: lineTotal,
    maxTotal: lineTotal,
  });
}

export function withSyncedLineDiscount(row, rules = []) {
  const amount = resolveLineDiscountAmount(row, rules);
  return {
    ...row,
    discount: amount > 0 ? String(amount) : "",
  };
}

export function lineHasDiscountIntent(row) {
  if (Number(row?.discount) > 0) return true;
  if (Boolean(row?.discount_rule_id)) return true;
  if (
    row?.discount_tab === "custom" &&
    row?.discount_custom_amount !== "" &&
    row?.discount_custom_amount !== null &&
    row?.discount_custom_amount !== undefined
  ) {
    return isValidCustomDiscountAmount(row.discount_custom_amount);
  }
  return Boolean(String(row?.discount_reason || "").trim());
}

export function validateLineItemDiscounts(lineItems = [], rules = []) {
  for (const row of lineItems) {
    const label =
      String(row.description || "Line item").trim() || "Line item";
    const discount = resolveLineDiscountAmount(row, rules);
    const lineTotal = getLineTotalBeforeDiscount(row);
    const gross = getLineGrossAmount(row);
    const reason = String(row.discount_reason || "").trim();

    if (!lineHasDiscountIntent(row)) continue;

    if (gross <= 0) {
      return {
        valid: false,
        error: `Add unit price before applying a discount on ${label}.`,
        lineId: row.id,
      };
    }

    if (row.discount_tab === "predefined" && !row.discount_rule_id) {
      return {
        valid: false,
        error: `Select a discount rule for ${label}.`,
        lineId: row.id,
      };
    }

    if (
      row.discount_tab === "custom" &&
      !isValidCustomDiscountAmount(row.discount_custom_amount)
    ) {
      return {
        valid: false,
        error: `Enter a valid custom discount for ${label}.`,
        lineId: row.id,
      };
    }

    if (!reason) {
      return {
        valid: false,
        error: `Add a discount reason for ${label}.`,
        lineId: row.id,
      };
    }

    if (discount > lineTotal) {
      return {
        valid: false,
        error: `${label} discount cannot exceed ${lineTotal}.`,
        lineId: row.id,
      };
    }
  }

  return { valid: true, error: "", lineId: "" };
}

export function validateLineItemDiscountForm(row, rules = []) {
  const result = validateLineItemDiscounts([row], rules);
  return {
    valid: result.valid,
    error: result.error,
    lineId: result.lineId || row?.id || "",
  };
}

export function displayLineItemInputValue(value) {
  if (value === "" || value === null || value === undefined) return "";
  return String(value);
}

export function computeLineItemAmount({
  quantity = 1,
  unitPrice = 0,
  discount = 0,
  taxRate = 0,
  taxType = "notax",
  /**
   * Commission tuition lines: total = qty × price; GST on that total;
   * discount is saved but does not reduce the line amount.
   */
  commissionLineTotals = false,
}) {
  const qty = Math.max(Number(quantity) || 0, 0);
  const unit = Number(unitPrice) || 0;
  const disc = Math.max(Number(discount) || 0, 0);
  const rate = Number(taxRate) || 0;
  const gross = roundMoney(unit * qty);
  const effectiveTaxType = normalizeTaxType(taxType);

  if (effectiveTaxType === "notax" || rate <= 0) {
    const totalBeforeDiscount = gross;
    const amount = Math.max(
      roundMoney(commissionLineTotals ? gross : totalBeforeDiscount - disc),
      0,
    );

    return {
      amount,
      tax_amount: 0,
      net: gross,
      total_before_discount: totalBeforeDiscount,
    };
  }

  const breakdown = calculateGstBreakdown({
    baseCost: gross,
    gstPercentage: rate,
    taxType: effectiveTaxType,
  });

  if (commissionLineTotals) {
    return {
      amount: gross,
      tax_amount: roundMoney(breakdown.gst_amount),
      net: roundMoney(breakdown.base_cost),
      total_before_discount: gross,
    };
  }

  const totalBeforeDiscount =
    effectiveTaxType === "exclusive"
      ? breakdown.professional_cost
      : breakdown.base_cost;
  const amount = Math.max(roundMoney(totalBeforeDiscount - disc), 0);

  return {
    amount,
    tax_amount: roundMoney(breakdown.gst_amount),
    net: roundMoney(breakdown.base_cost),
    total_before_discount: roundMoney(totalBeforeDiscount),
  };
}

export function buildComputedLineItem(row, taxType) {
  const lineTaxType = normalizeTaxType(row.tax_type) || normalizeTaxType(taxType) || "notax";
  const storedDiscount = Math.max(Number(row.discount) || 0, 0);
  const commissionInstallmentLine = isCommissionInstallmentInvoiceLine(row);
  const discountForAmount = commissionInstallmentLine ? 0 : storedDiscount;
  const { amount, tax_amount, net } = computeLineItemAmount({
    quantity: row.quantity,
    unitPrice: row.unit_price,
    discount: discountForAmount,
    taxRate: row.tax_rate,
    taxType: lineTaxType,
    commissionLineTotals: commissionInstallmentLine,
  });

  return {
    ...row,
    quantity: Math.max(Number(row.quantity) || 0, 0),
    unit_price: Number(row.unit_price) || 0,
    discount: storedDiscount,
    tax_rate: lineTaxType === "notax" ? 0 : Number(row.tax_rate) || 0,
    tax_type: lineTaxType,
    amount,
    tax_amount,
    net,
  };
}

export function resolveLineItemPaymentAmount(line = {}) {
  const amount =
    line.amount ??
    line.line_total ??
    line.lineTotal ??
    line.total ??
    line.net;

  return Math.max(Number(amount) || 0, 0);
}

/** Line balance for payments — includes exclusive GST when applicable. */
export function resolveLineItemPayableAmount(line = {}) {
  const base = resolveLineItemPaymentAmount(line);
  const taxType = normalizeTaxType(line.tax_type || line.taxType) || "notax";
  if (taxType === "exclusive") {
    return roundMoney(
      base + Math.max(Number(line.tax_amount ?? line.taxAmount) || 0, 0),
    );
  }
  return roundMoney(base);
}

export function resolveInvoiceDocumentTaxType(lineItems = []) {
  const types = [
    ...new Set(
      lineItems
        .map((row) => normalizeTaxType(row.tax_type) || "notax")
        .filter(Boolean),
    ),
  ];

  if (types.length === 1) return types[0];
  if (types.length > 1) return "mixed";
  return "notax";
}

export function getLineAdditiveTax(row) {
  const lineTaxType = normalizeTaxType(row.tax_type) || "notax";
  if (lineTaxType === "exclusive") {
    return Number(row.tax_amount) || 0;
  }
  return 0;
}

export function getLineInclusiveTax(row) {
  const lineTaxType = normalizeTaxType(row.tax_type) || "notax";
  if (lineTaxType === "inclusive") {
    return Number(row.tax_amount) || 0;
  }
  return 0;
}

export function getLineExclusiveTax(row) {
  const lineTaxType = normalizeTaxType(row.tax_type) || "notax";
  if (lineTaxType === "exclusive") {
    return Number(row.tax_amount) || 0;
  }
  return 0;
}

export function resolveSummaryTaxDisplay(lineItems = []) {
  const rows = Array.isArray(lineItems) ? lineItems : [];
  const inclusive_tax_amount = roundMoney(
    rows.reduce((sum, row) => sum + getLineInclusiveTax(row), 0),
  );
  const exclusive_tax_amount = roundMoney(
    rows.reduce((sum, row) => sum + getLineExclusiveTax(row), 0),
  );
  const hasInclusive = inclusive_tax_amount > 0;
  const hasExclusive = exclusive_tax_amount > 0;

  let summary_tax_mode = "notax";
  if (hasInclusive && hasExclusive) {
    summary_tax_mode = "mixed";
  } else if (hasExclusive) {
    summary_tax_mode = "exclusive";
  } else if (hasInclusive) {
    summary_tax_mode = "inclusive";
  }

  return {
    inclusive_tax_amount,
    exclusive_tax_amount,
    summary_tax_mode,
  };
}

export function calculateInvoiceLineTotals(lineItems, taxType) {
  const computed = lineItems.map((row) => buildComputedLineItem(row, taxType));
  const gross_subtotal = roundMoney(
    computed.reduce(
      (sum, row) => sum + (row.quantity || 0) * (row.unit_price || 0),
      0,
    ),
  );
  const discount_total = roundMoney(
    computed.reduce((sum, row) => sum + (row.discount || 0), 0),
  );
  const subtotal = roundMoney(
    computed.reduce((sum, row) => sum + (row.amount || 0), 0),
  );
  const tax_amount = roundMoney(
    computed.reduce((sum, row) => sum + (row.tax_amount || 0), 0),
  );
  const total_amount = roundMoney(
    computed.reduce((sum, row) => sum + (row.amount || 0), 0),
  );
  const summaryTax = resolveSummaryTaxDisplay(computed);

  return {
    lineItems: computed,
    gross_subtotal,
    discount_total,
    subtotal,
    tax_amount,
    total_amount,
    ...summaryTax,
  };
}

export function formatInvoiceMoney(
  value,
  currency = "",
  {
    minDecimals = INVOICE_AMOUNT_MIN_DECIMALS,
    maxDecimals = INVOICE_AMOUNT_MAX_DECIMALS,
    decimals,
  } = {},
) {
  const num = roundInvoiceAmount(value);
  const safeNum = Number.isNaN(num) ? 0 : num;
  const minFractionDigits = decimals != null ? decimals : minDecimals;
  const maxFractionDigits = decimals != null ? decimals : maxDecimals;
  const formatted = safeNum.toLocaleString(undefined, {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  });
  return currency ? `${currency} ${formatted}` : formatted;
}

export function formatInvoiceNumberDisplay(value) {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return formatInvoiceMoney(num, "", {});
}

export function formatTaxRateLabel(rate, taxType) {
  if (normalizeTaxType(taxType) === "notax") return "0%";
  const num = Number(rate);
  if (!num || Number.isNaN(num)) return "0%";
  return `${num}%`;
}

export function formatLineItemTaxRateDisplay(rate, taxType) {
  const type = normalizeTaxType(taxType);
  if (type === "notax") return "0%";

  const num = Number(rate);
  const rateLabel = num && !Number.isNaN(num) ? `${num}%` : "0%";

  if (type === "inclusive") return `${rateLabel} Inc.`;
  if (type === "exclusive") return `${rateLabel} Exc.`;

  return rateLabel;
}

export function getLineTaxTypeShortLabel(taxType) {
  const type = normalizeTaxType(taxType);
  if (type === "inclusive") return "Inc";
  if (type === "exclusive") return "Excl";
  return "";
}

export function getInvoiceSummaryTaxLabel(taxType) {
  const type = normalizeTaxType(taxType);
  if (type === "inclusive") return "Includes GST on income";
  if (type === "exclusive") return "GST on income";
  return "";
}

/** Summary tax row label from line tax types (falls back to document tax type). */
export function resolveInvoiceSummaryTaxLabel(
  lineItems = [],
  documentTaxType = "",
) {
  const rows = Array.isArray(lineItems) ? lineItems : [];
  const lineType = resolveInvoiceDocumentTaxType(rows);

  if (lineType === "exclusive") return "GST on income";
  if (lineType === "inclusive") return "Includes GST on income";

  if (lineType === "mixed") {
    const { exclusive_tax_amount, inclusive_tax_amount } =
      resolveSummaryTaxDisplay(rows);
    if (exclusive_tax_amount > 0) return "GST on income";
    if (inclusive_tax_amount > 0) return "Includes GST on income";
    return "";
  }

  return getInvoiceSummaryTaxLabel(documentTaxType);
}

/**
 * Ex-tax invoice footer: exclusive GST is added to total; inclusive GST is
 * shown only (not added again).
 */
export function resolveExTaxFooterTotals(
  lineItems = [],
  documentTaxType = "",
) {
  const rows = Array.isArray(lineItems) ? lineItems : [];
  const subtotal = sumLineDisplayTotalAmounts(rows);
  const {
    inclusive_tax_amount,
    exclusive_tax_amount,
    summary_tax_mode,
  } = resolveSummaryTaxDisplay(rows);

  if (summary_tax_mode === "exclusive") {
    return {
      subtotalExTax: subtotal,
      gst: exclusive_tax_amount,
      total: roundMoney(subtotal + exclusive_tax_amount),
      taxLabel: "GST on income",
      summary_tax_mode,
      addsTaxToTotal: true,
    };
  }

  if (summary_tax_mode === "inclusive") {
    return {
      subtotalExTax: subtotal,
      gst: inclusive_tax_amount,
      total: subtotal,
      taxLabel: "Includes GST on income",
      summary_tax_mode,
      addsTaxToTotal: false,
    };
  }

  if (summary_tax_mode === "mixed") {
    return {
      subtotalExTax: subtotal,
      gst: roundMoney(inclusive_tax_amount + exclusive_tax_amount),
      total: roundMoney(subtotal + exclusive_tax_amount),
      taxLabel: "GST on income",
      summary_tax_mode,
      addsTaxToTotal: true,
    };
  }

  return {
    subtotalExTax: subtotal,
    gst: 0,
    total: subtotal,
    taxLabel: resolveInvoiceSummaryTaxLabel(rows, documentTaxType),
    summary_tax_mode,
    addsTaxToTotal: false,
  };
}

/** Invoice amount due / summary total — includes exclusive GST when applicable. */
export function resolvePayableInvoiceTotal({
  effectiveTotals = null,
  xeroExTaxDisplayTotals = null,
  lineTotals = null,
  storedTotal = null,
} = {}) {
  const xeroTotal = Number(xeroExTaxDisplayTotals?.total) || 0;
  if (xeroTotal > 0) return xeroTotal;

  const lineItems = lineTotals?.lineItems;
  if (Array.isArray(lineItems) && lineItems.length > 0) {
    const footerTotal = Number(resolveExTaxFooterTotals(lineItems).total) || 0;
    if (footerTotal > 0) return footerTotal;
  }

  const effectiveTotal = Number(effectiveTotals?.total_amount) || 0;
  if (effectiveTotal > 0) return effectiveTotal;

  return Math.max(Number(storedTotal) || 0, 0);
}

function resolveRecordLineItems(invoice = {}) {
  if (!invoice || typeof invoice !== "object") return [];

  const nested =
    invoice.invoice && typeof invoice.invoice === "object"
      ? invoice.invoice
      : null;
  const sources = nested ? [nested, invoice] : [invoice];

  for (const source of sources) {
    for (const key of ["line_items", "lineItems", "items"]) {
      const items = source[key];
      if (Array.isArray(items) && items.length) return items;
    }
  }

  return [];
}

/**
 * Payable invoice totals from a stored/API record — includes exclusive GST
 * when line items (or header tax fields) indicate ex-tax pricing.
 */
export function resolveInvoiceRecordPayableTotals(invoice = {}) {
  const lineItems = resolveRecordLineItems(invoice);
  const paid = Math.max(
    Number(
      invoice.paid_amount ??
        invoice.paidAmount ??
        invoice.totals?.paid_amount,
    ) || 0,
    0,
  );
  const storedTotal = Math.max(
    Number(
      invoice.total_amount ??
        invoice.totalAmount ??
        invoice.amount ??
        invoice.totals?.total_amount,
    ) || 0,
    0,
  );
  const storedDue =
    invoice.amount_due ?? invoice.amountDue ?? invoice.totals?.amount_due;

  let payableTotal = storedTotal;

  if (lineItems.length > 0) {
    const computedTotal =
      Number(resolveExTaxFooterTotals(lineItems).total) || 0;
    if (computedTotal > 0) {
      payableTotal = computedTotal;
    }
  } else {
    const taxTotal = Math.max(
      Number(
        invoice.tax_total ??
          invoice.taxTotal ??
          invoice.tax_amount ??
          invoice.totals?.tax_total ??
          invoice.totals?.tax_amount,
      ) || 0,
      0,
    );
    const exclusiveTax = Math.max(
      Number(invoice.exclusive_tax_amount ?? invoice.exclusiveTaxAmount) || 0,
      0,
    );
    const docTaxType = normalizeTaxType(
      invoice.tax_type ?? invoice.gst_status ?? invoice.totals?.tax_type,
    );

    if (docTaxType === "exclusive" && taxTotal > 0 && storedTotal > 0) {
      payableTotal = roundMoney(storedTotal + taxTotal);
    } else if (exclusiveTax > 0 && storedTotal > 0) {
      payableTotal = roundMoney(storedTotal + exclusiveTax);
    }
  }

  const recomputedDue = Math.max(roundMoney(payableTotal - paid), 0);
  const usedLineItems = lineItems.length > 0;
  const adjustedTotal = usedLineItems && payableTotal > 0;

  let amountDue = recomputedDue;
  if (!usedLineItems && !adjustedTotal) {
    if (storedDue !== null && storedDue !== undefined && storedDue !== "") {
      amountDue = Math.max(Number(storedDue) || 0, 0);
    }
  }

  return {
    total_amount: payableTotal,
    amount_due: amountDue,
    paid_amount: paid,
  };
}

export function resolvePreviewTaxLabel({
  lineItems = [],
  inclusiveTaxAmount = 0,
  exclusiveTaxAmount = 0,
  taxAmount = 0,
  documentTaxType = "",
} = {}) {
  const rows = Array.isArray(lineItems) ? lineItems : [];
  const footerTotals = resolveExTaxFooterTotals(rows, documentTaxType);
  const label = footerTotals.taxLabel;
  if (!label) return "";

  const hasTax =
    Number(exclusiveTaxAmount) > 0 ||
    Number(inclusiveTaxAmount) > 0 ||
    (Number(taxAmount) || 0) > 0 ||
    footerTotals.gst > 0;

  return hasTax ? label : "";
}
